let PIXI;
if (typeof window !== 'undefined') {
  PIXI = require('pixi.js');
}
import { TweenLite } from 'gsap';
import CustomEase from '../../lib/CustomEase';
import PixiPlugin from 'gsap/PixiPlugin';
import getCoverSize from './utils/getCoverSize';

CustomEase.create('swoop', '0.5, 0, 0.5, 1');
const tabletBreakpoint = 992;
const mobileBreakpoint = 768;
const AUTOPLAYER_HEIGHT = 45;

class FramedImage {
  constructor(
    app,
    imgSrc,
    addSelfToStage,
    focalX = 0.5,
    focalY = 0.5,
    screenAlpha = 0
  ) {
    // reference to PIXI app
    this.addSelfToStage = addSelfToStage;
    this.app = app;
    this.imgSrc = imgSrc;
    this.screenAlpha = screenAlpha;
    this.focalPoint = { x: focalX, y: focalY };
    this.isContracted = false;
    // how much does slide contract during slider transition
    this.frameContractAmount = 0.87;
    this.imgContractAmount = 0.95;

    this.loader = new PIXI.loaders.Loader();
  }
  load() {
    return new Promise((resolve, reject) => {
      if (!this.loader.resources[this.imgSrc]) {
        this.loader.add(this.imgSrc).load(() => {
          this.setup(resolve);
        });
      } else {
        this.setup(resolve);
      }
    });
  }
  setup = callback => {
    this.sprite = new PIXI.Sprite(this.loader.resources[this.imgSrc].texture);
    this.imgContainer = new PIXI.Container();
    this.imgContainer.addChild(this.sprite);
    this.mask = new PIXI.Graphics();
    this.mask.beginFill(0x000000);
    this.mask.drawRect(0, 0, 1, 1);
    this.mask.endFill();

    this.screen = new PIXI.Graphics();
    this.screen.beginFill(0x000000);
    this.screen.drawRect(0, 0, 1, 1);
    this.screen.endFill();
    this.screen.scale.x = window.innerWidth;
    this.screen.scale.y = window.innerHeight;

    this.screen.alpha = this.screenAlpha;
    this.imgContainer.addChild(this.screen);
    this.imgContainer.mask = this.mask;

    if (this.addSelfToStage) {
      this.app.stage.addChild(this.mask);
      this.app.stage.addChild(this.imgContainer);
    }

    this.naturalSize = {
      width: this.sprite.texture.orig.width,
      height: this.sprite.texture.orig.height,
    };
    this.imgContainer.visible = false;
    if (callback) {
      callback();
    }
  };

  sliderSize(contracted) {
    // if contracted, get the size it woudl be in the contracted slider
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    let width, height, x, y;

    if (windowWidth >= tabletBreakpoint) {
      // desktop size
      // 62% of a centered 92% container
      const containerSize = windowWidth * 0.92;

      width = containerSize * 0.62 - containerSize * 0.07;
      height = windowHeight - (170 + AUTOPLAYER_HEIGHT); //100px from top and 70px from bottom
      // x = windowWidth * 0.4 + windowWidth * 0.92 * 0.38;
      x = windowWidth * 0.04 + containerSize * 0.38;
      y = 100 + AUTOPLAYER_HEIGHT * 0.5;
    } else if (windowWidth >= mobileBreakpoint) {
      width = windowWidth - 60;
      height = windowHeight - (130 + AUTOPLAYER_HEIGHT);
      x = 30;
      y = 100;
    } else {
      // mobile: 30px pad
      width = windowWidth - 40;
      height = windowHeight - (127 + AUTOPLAYER_HEIGHT);
      x = 20;
      y = 107;
    }
    if (contracted) {
      width *= this.frameContractAmount;
      height *= this.frameContractAmount;
      x += (1 - this.frameContractAmount) * width / 2;
      y += (1 - this.frameContractAmount) * height / 2;
    }
    return [width, height, x, y];
  }
  // contract and expand between for slider transition
  contract(inverse = false, duration = 0.75) {
    if ((this.isContracted && !inverse) || (!this.isContracted && inverse)) {
      return;
    }
    this.isContracted = !inverse;
    const scaleAmount = inverse
      ? 1 / this.frameContractAmount
      : this.frameContractAmount;
    const startLeft = this.mask.position.x;
    const startTop = this.mask.position.y;
    const startWidth = this.mask.scale.x;
    const startHeight = this.mask.scale.y;
    const scaleX = this.mask.scale.x * scaleAmount;
    const scaleY = this.mask.scale.y * scaleAmount;
    TweenLite.to(this.mask, duration, {
      pixi: {
        x: startLeft + (startWidth - scaleX) / 2,
        y: startTop + (startHeight - scaleY) / 2,
        scaleX: scaleX,
        scaleY: scaleY,
      },
      ease: Power1.easeInOut,
    });

    const imgScaleAmount = inverse
      ? 1 / this.imgContractAmount
      : this.imgContractAmount;
    const imgStartScale = this.sprite.scale.x;
    const imgStartLeft = this.sprite.position.x;
    const imgStartTop = this.sprite.position.y;
    const imgStartWidth = this.naturalSize.width * imgStartScale;
    const imgStartHeight = this.naturalSize.height * imgStartScale;
    const imgScale = this.sprite.scale.x * imgScaleAmount;
    const imgFinishWidth = this.naturalSize.width * imgScale;
    const imgFinishHeight = this.naturalSize.height * imgScale;

    TweenLite.to(this.sprite, duration, {
      pixi: {
        scale: imgScale,
        x: imgStartLeft - (imgFinishWidth - imgStartWidth) / 2,
        y: imgStartTop - (imgFinishHeight - imgStartHeight) / 2,
      },
      ease: Power1.easeInOut,
    });
  }
  expand(duration) {
    // inverse of contract
    this.contract(true, duration);
  }
  setVisible(bool = true) {
    this.imgContainer.visible = bool;
  }
  drawAtSliderSize() {
    const alpha = window.innerWidth < tabletBreakpoint ? 0.4 : 0;
    this.setImageSize(this.getStartSize('full'));
    this.panDownTo(...this.sliderSize(), 0, null, alpha);

    this.imgContainer.visible = true;
  }
  screenToSlider(
    type,
    drawCallback,
    completeCallback,
    duration = 2,
    startScreenAlpha = 0.4,
    endScreenAlpha = 0,
    currentSlideIndex
  ) {
    // promise so you can chain with .then
    return new Promise((resolve, reject) => {
      const startSize = this.getStartSize(type, currentSlideIndex);
      this.setImageSize(startSize);
      this.screen.alpha = startScreenAlpha;
      this.imgContainer.visible = true;
      if (drawCallback) {
        drawCallback();
      }
      this.panDownTo(
        ...this.sliderSize(true),
        duration,
        resolve,
        endScreenAlpha
      );
    });
  }
  chapterOpenPan(onComplete, alpha = 0) {
    const size = this.getCoverSize({
      width: window.innerWidth,
      height: window.innerHeight,
      x: 0,
      y: 0,
    });

    this.panTo(
      size.imgSize.scale,
      size.imgSize.transform.x,
      size.imgSize.transform.y,
      size.frameSize.width,
      size.frameSize.height,
      size.frameSize.x,
      size.frameSize.y,
      1,
      onComplete,
      alpha,
      'swoop'
    );
  }
  // {width: , height: , x: , y: ,}
  panToCoverSize(
    size,
    duration = 1,
    onComplete,
    isImageFixed = false,
    screenAlpha = 0
  ) {
    const coverSize = this.getCoverSize(size);
    const imgFullSize = this.getCoverSize({
      width: window.innerWidth,
      height: window.innerHeight,
      x: 0,
      y: 0,
    });

    this.panTo(
      isImageFixed ? imgFullSize.imgSize.scale : coverSize.imgSize.scale,
      isImageFixed
        ? imgFullSize.imgSize.transform.x
        : coverSize.imgSize.transform.x,
      isImageFixed
        ? imgFullSize.imgSize.transform.y
        : coverSize.imgSize.transform.y,
      coverSize.frameSize.width,
      coverSize.frameSize.height,
      coverSize.frameSize.x,
      coverSize.frameSize.y,
      duration,
      onComplete,
      screenAlpha
    );
  }
  panDownTo(
    width,
    height,
    x = 0,
    y = 0,
    duration = 1.25,
    callback,
    alpha = 0,
    ease = Power3.easeInOut
  ) {
    // Pan from fullwidth size to a new size, using focal point

    // for now, go from what is full bleed scale to 90% of that
    const scaleAmount = 0.9;

    const targetScale = this.sprite.scale.x * scaleAmount;
    const frameCenter = {
      x: x + width / 2,
      y: y + height / 2,
    };
    const scaledWidth = this.naturalSize.width * targetScale;
    const scaledHeight = this.naturalSize.height * targetScale;
    let imgX, imgY;

    // figure out if at its scaled size, if you centered the focal point of the image
    // would you end up seeing the edge. If so, push it back to be flush agasint that edge
    if (scaledWidth * this.focalPoint.x <= width / 2) {
      // make it flush left
      imgX = x;
    } else if (scaledWidth * (1 - this.focalPoint.x) <= width / 2) {
      // make it flush right
      imgX = width + x - scaledWidth;
    } else {
      // center focal x
      imgX = frameCenter.x - scaledWidth * this.focalPoint.x;
    }
    if (scaledHeight * this.focalPoint.y <= height / 2) {
      // make it flush top
      imgY = y;
    } else if (scaledHeight * (1 - this.focalPoint.y) <= height / 2) {
      // make it flush bottom
      imgY = height + y - scaledHeight;
    } else {
      // center focal y
      imgY = frameCenter.y - scaledHeight * this.focalPoint.y;
    }
    this.panTo(
      targetScale,
      imgX,
      imgY,
      width,
      height,
      x,
      y,
      duration,
      callback,
      alpha,
      ease
    );
  }
  panTo(
    imgScale,
    imgX,
    imgY,
    maskWidth,
    maskHeight,
    maskX,
    maskY,
    duration = 1,
    callback,
    screenAlpha,
    ease = 'swoop'
  ) {
    TweenLite.to(this.screen, duration, {
      pixi: {
        alpha: screenAlpha,
      },
      ease: ease,
    });
    TweenLite.to(this.sprite, duration, {
      pixi: {
        scale: imgScale,
        x: imgX,
        y: imgY,
      },
      ease: ease,
      onComplete: () => {
        if (callback) callback();
      },
    });
    TweenLite.to(this.mask, duration, {
      pixi: {
        x: maskX,
        y: maskY,
        scaleX: maskWidth,
        scaleY: maskHeight,
      },
      ease: ease,
    });
  }
  getStartSize(type, currentSlideIndex) {
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    switch (type) {
      case 'half-left':
        if (windowWidth <= 768) {
          if (typeof currentSlideIndex !== 'undefined') {
            if (currentSlideIndex === 0) {
              return {
                width: windowWidth,
                height: windowHeight,
                x: 0,
                y: 0,
              };
            }
          }
          return {
            width: windowWidth,
            height: windowHeight * 0.7,
            x: 0,
            y: 0,
          };
        } else if (currentSlideIndex === 0) {
          return {
            width: windowWidth,
            height: windowHeight,
            x: 0,
            y: 0,
          };
        }
        return {
          width: windowWidth / 2,
          height: windowHeight,
          x: 0,
          y: 0,
        };
      case 'half-right':
        if (windowWidth <= 768) {
          if (typeof currentSlideIndex !== 'undefined') {
            if (currentSlideIndex === 0) {
              return {
                width: windowWidth,
                height: windowHeight,
                x: 0,
                y: 0,
              };
            }
          }
          return {
            width: windowWidth,
            height: windowHeight * 0.7,
            x: 0,
            y: 0,
          };
        } else if (currentSlideIndex === 0) {
          return {
            width: windowWidth,
            height: windowHeight,
            x: 0,
            y: 0,
          };
        }
        return {
          width: windowWidth / 2,
          height: windowHeight,
          x: windowWidth / 2,
          y: 0,
        };
      case 'center':
        return {
          width: windowWidth * 0.46,
          height: windowHeight,
          x: windowWidth * 0.27,
          y: 0,
        };
      default:
        // full width
        return {
          width: window.innerWidth,
          height: window.innerHeight,
          x: 0,
          y: 0,
        };
    }
  }
  // { width:_ , height:_, x:_, y:_ }
  setImageSize(frameSize, isImageFixed = false, screenAlpha = 0) {
    const dimensions = this.getCoverSize(frameSize);
    this.mask.scale.set(
      dimensions.frameSize.width,
      dimensions.frameSize.height
    );
    this.mask.position.set(dimensions.frameSize.x, dimensions.frameSize.y);
    this.screen.alpha = screenAlpha;
    this.screen.scale.x = window.innerWidth;
    this.screen.scale.y = window.innerHeight;
    if (isImageFixed) {
      const imgDimensions = this.getCoverSize({
        width: window.innerWidth,
        height: window.innerHeight,
        x: 0,
        y: 0,
      });
      this.sprite.scale.set(imgDimensions.imgSize.scale);
      this.sprite.position.set(
        imgDimensions.imgSize.transform.x,
        imgDimensions.imgSize.transform.y
      );
    } else {
      this.sprite.scale.set(dimensions.imgSize.scale);
      this.sprite.position.set(
        dimensions.imgSize.transform.x,
        dimensions.imgSize.transform.y
      );
    }
  }

  // { width:_ , height:_, x:_, y:_ }
  getCoverSize(frameSize) {
    // given a frame size and position, get the image transform and scale to match
    // a CSS cover
    return {
      frameSize,
      imgSize: getCoverSize(frameSize, this.naturalSize),
    };
  }
}

export default FramedImage;



// WEBPACK FOOTER //
// ./src/components/pixi/FramedImage.js